import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { FiArrowLeft, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { BiSolidCoinStack } from "react-icons/bi";

import { useAuth } from "../contexts/AuthContext.jsx";
import { useQueryClient } from "@tanstack/react-query";
import {
  useUserProfile,
  useCreateOrGetUser,
  useCreateBillingPortalSession,
  useBillingPackages,
  useCreateCheckoutSession,
  QUERY_KEYS,
} from "../hooks/useApi.js";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, signInWithGoogle, signOut, refreshUser } = useAuth();
  const { data: userProfile, isLoading: profileLoading, error: userProfileError } = useUserProfile();
  const createOrGetUserMutation = useCreateOrGetUser();
  const createBillingPortalMutation = useCreateBillingPortalSession();
  const { data: packages, isLoading: packagesLoading } = useBillingPackages();
  const createCheckoutMutation = useCreateCheckoutSession();
  const queryClient = useQueryClient();

  const [paymentResult, setPaymentResult] = useState(null); // "success" | "error" | null
  const [error, setError] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedTopupId, setSelectedTopupId] = useState("");
  const [billingTab, setBillingTab] = useState("plans");
  const hasInitiatedMutation = useRef(false);

  const subscriptions = packages?.subscriptions || [];
  const topups = packages?.topups || [];

  // Auto-create user profile if needed
  useEffect(() => {
    if (user && !profileLoading && !userProfile && userProfileError && !hasInitiatedMutation.current) {
      hasInitiatedMutation.current = true;
      createOrGetUserMutation.mutate(undefined, {
        onError: () => {
          refreshUser();
        },
      });
    }
  }, [user, userProfile, profileLoading, userProfileError, refreshUser, createOrGetUserMutation]);

  useEffect(() => {
    hasInitiatedMutation.current = false;
  }, [user]);

  // Check for payment result in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("success") === "true") {
      setPaymentResult("success");
      if (user) refreshUser();
      // Invalidate profile cache so subscription status refreshes
      // Small delay to give webhook time to process
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PROFILE] });
      }, 2000);
      const url = new URL(window.location);
      url.searchParams.delete("success");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url);
    } else if (urlParams.get("error") === "true") {
      setPaymentResult("error");
      const url = new URL(window.location);
      url.searchParams.delete("error");
      window.history.replaceState({}, "", url);
    }
  }, [user, refreshUser]);

  // Set defaults
  useEffect(() => {
    if (subscriptions.length > 0 && !selectedPlanId) {
      const defaultPlan = subscriptions.find((p) => p.mostPopular) || subscriptions[0];
      setSelectedPlanId(defaultPlan.id);
    }
  }, [subscriptions, selectedPlanId]);

  useEffect(() => {
    if (topups.length > 0 && !selectedTopupId) {
      setSelectedTopupId(topups[0].id);
    }
  }, [topups, selectedTopupId]);

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (err) {
      setError("Failed to sign in. Please try again.");
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      setError("Failed to sign out. Please try again.");
    }
  };

  const handleManageSubscription = async () => {
    if (!user || !userProfile) return;
    try {
      setError("");
      await createBillingPortalMutation.mutateAsync();
    } catch (err) {
      setError("Failed to open billing portal. Please try again.");
    }
  };

  const handleCheckout = async (priceId) => {
    if (!priceId) return;
    try {
      setError("");
      await createCheckoutMutation.mutateAsync(priceId);
    } catch (err) {
      setError("Failed to create checkout session. Please try again.");
    }
  };

  const credits = (userProfile?.credits_remaining || 0) + (userProfile?.topup_credits || 0);
  const hasActiveSubscription = userProfile?.subscription_status === "active";

  // Switch to topups tab if user already has a subscription
  useEffect(() => {
    if (hasActiveSubscription && billingTab === "plans") {
      setBillingTab("topups");
    }
  }, [hasActiveSubscription, billingTab]);

  const selectedPlan = subscriptions.find((p) => p.id === selectedPlanId);
  const selectedTopup = topups.find((p) => p.id === selectedTopupId);

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#fff",
        fontFamily: "'Inter', sans-serif",
        color: "#111",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px clamp(20px, 5vw, 40px)",
          borderBottom: "1px solid #eee",
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        <button
          onClick={() => window.history.length > 1 ? navigate(-1) : navigate("/")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
            color: "#555",
          }}
        >
          <FiArrowLeft size={18} />
          Back
        </button>
        <div style={{ fontWeight: 800, fontSize: "18px", letterSpacing: "-0.02em" }}>
          scribbledraw.
        </div>
      </div>

      <div
        style={{
          maxWidth: "560px",
          margin: "0 auto",
          padding: "48px clamp(20px, 5vw, 40px)",
        }}
      >
        {/* Payment result screen */}
        {paymentResult && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                marginBottom: "8px",
              }}
            >
              {paymentResult === "success" ? (
                <FiCheckCircle size={28} color="#000" />
              ) : (
                <FiXCircle size={28} color="#000" />
              )}
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  margin: 0,
                }}
              >
                {paymentResult === "success" ? "Payment successful" : "Payment failed"}
              </h2>
            </div>
            <p style={{ color: "#888", fontSize: "15px", marginBottom: "32px" }}>
              {paymentResult === "success"
                ? "Your credits have been added to your account."
                : "Something went wrong. Please try again."}
            </p>
            <button
              onClick={() => setPaymentResult(null)}
              style={{
                padding: "10px 24px",
                fontSize: "14px",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: "#000",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
              }}
            >
              Continue
            </button>
          </div>
        )}

        {/* Inline error for non-payment actions */}
        {error && !paymentResult && (
          <div
            style={{
              padding: "12px 16px",
              marginBottom: "24px",
              borderRadius: "10px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {/* Loading state */}
        {paymentResult ? null : profileLoading || createOrGetUserMutation.isPending ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "3px solid #eee",
                borderTopColor: "#000",
                borderRadius: "50%",
                animation: "spin 0.6s linear infinite",
                margin: "0 auto 16px",
              }}
            />
            <p style={{ color: "#999", fontSize: "14px" }}>Loading...</p>
          </div>
        ) : !user ? (
          /* Signed out state */
          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                marginBottom: "8px",
              }}
            >
              Sign in
            </h1>
            <p style={{ color: "#888", fontSize: "16px", marginBottom: "32px" }}>
              Sign in to start creating and manage your credits
            </p>
            <button
              onClick={handleSignIn}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                padding: "14px 28px",
                fontSize: "15px",
                fontWeight: 600,
                color: "#333",
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#bbb";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#ddd";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <FcGoogle size={22} />
              Continue with Google
            </button>
          </div>
        ) : (
          /* Signed in state */
          <div>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                marginBottom: "4px",
              }}
            >
              Account
            </h1>
            <p style={{ color: "#888", fontSize: "15px", marginBottom: "36px" }}>
              {user.email}
            </p>

            {/* Credits card */}
            <div
              style={{
                padding: "24px",
                borderRadius: "14px",
                border: "1px solid #eee",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontSize: "14px", fontWeight: 500, color: "#888", marginBottom: "2px" }}>
                  Credits available
                </div>
                <div style={{ fontSize: "36px", fontWeight: 800, letterSpacing: "-0.02em" }}>
                  {credits.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BiSolidCoinStack size={24} color="#555" />
              </div>
            </div>

            {/* Subscription status */}
            {hasActiveSubscription && (
              <div
                style={{
                  padding: "24px",
                  borderRadius: "14px",
                  border: "1px solid #eee",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div style={{ fontSize: "14px", fontWeight: 500, color: "#888", marginBottom: "2px" }}>
                    Subscription
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "16px", fontWeight: 600 }}>
                      {userProfile?.subscription_type || "Active Plan"}
                    </span>
                    <span
                      style={{
                        padding: "2px 8px",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#166534",
                        backgroundColor: "#dcfce7",
                        borderRadius: "9999px",
                      }}
                    >
                      Active
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleManageSubscription}
                  disabled={createBillingPortalMutation.isPending}
                  style={{
                    padding: "8px 16px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#555",
                    backgroundColor: "#f5f5f5",
                    border: "none",
                    borderRadius: "8px",
                    cursor: createBillingPortalMutation.isPending ? "not-allowed" : "pointer",
                    opacity: createBillingPortalMutation.isPending ? 0.5 : 1,
                  }}
                >
                  {createBillingPortalMutation.isPending ? "Loading..." : "Manage"}
                </button>
              </div>
            )}

            {/* Get more credits — tabbed card */}
            <div
              style={{
                padding: "24px",
                borderRadius: "14px",
                border: "1px solid #eee",
                marginBottom: "36px",
              }}
            >
              <div style={{ fontSize: "16px", fontWeight: 700, marginBottom: "16px" }}>
                Get more credits
              </div>

              {/* Pill toggle */}
              <div
                style={{
                  display: "flex",
                  gap: "3px",
                  padding: "3px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "9999px",
                  marginBottom: "20px",
                }}
              >
                {(!hasActiveSubscription ? [
                  { key: "plans", label: "Plans" },
                  { key: "topups", label: "Credit Packs" },
                ] : [
                  { key: "topups", label: "Credit Packs" },
                ]).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setBillingTab(tab.key)}
                    style={{
                      flex: 1,
                      padding: "7px 16px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: billingTab === tab.key ? "#fff" : "#888",
                      backgroundColor: billingTab === tab.key ? "#000" : "transparent",
                      border: "none",
                      borderRadius: "9999px",
                      cursor: "pointer",
                      transition: "background-color 0.15s ease, color 0.15s ease",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {packagesLoading ? (
                <div style={{ textAlign: "center", padding: "20px 0", color: "#999", fontSize: "14px" }}>
                  Loading...
                </div>
              ) : billingTab === "plans" && !hasActiveSubscription ? (
                <>
                  <p style={{ fontSize: "13px", color: "#888", marginBottom: "16px" }}>
                    Monthly subscription with credits that refresh each cycle.
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "16px" }}>
                    {subscriptions.map((pkg) => (
                      <div
                        key={pkg.id}
                        onClick={() => setSelectedPlanId(pkg.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "14px 16px",
                          borderRadius: "10px",
                          border: selectedPlanId === pkg.id ? "2px solid #000" : "1px solid #eee",
                          cursor: "pointer",
                          transition: "border-color 0.15s",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <div
                            style={{
                              width: "18px",
                              height: "18px",
                              borderRadius: "50%",
                              border: selectedPlanId === pkg.id ? "5px solid #000" : "2px solid #ccc",
                              transition: "border 0.15s",
                            }}
                          />
                          <div>
                            <div style={{ fontSize: "15px", fontWeight: 600 }}>
                              {pkg.productName}
                            </div>
                            <div style={{ fontSize: "12px", color: "#888" }}>
                              {pkg.amount} credits / month
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ fontSize: "18px", fontWeight: 700 }}>
                            {pkg.formattedPrice}<span style={{ fontSize: "13px", fontWeight: 400, color: "#888" }}>/mo</span>
                          </span>
                          {pkg.mostPopular && (
                            <span
                              style={{
                                padding: "2px 8px",
                                fontSize: "10px",
                                fontWeight: 600,
                                color: "#fff",
                                backgroundColor: "#000",
                                borderRadius: "9999px",
                              }}
                            >
                              Popular
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => handleCheckout(selectedPlanId)}
                    disabled={createCheckoutMutation.isPending || !selectedPlan || packagesLoading}
                    style={{
                      width: "100%",
                      padding: "12px",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#fff",
                      backgroundColor: createCheckoutMutation.isPending || !selectedPlan ? "#ccc" : "#000",
                      border: "none",
                      borderRadius: "10px",
                      cursor: createCheckoutMutation.isPending || !selectedPlan ? "not-allowed" : "pointer",
                      transition: "background-color 0.15s",
                    }}
                  >
                    {createCheckoutMutation.isPending
                      ? "Loading..."
                      : selectedPlan
                        ? `Subscribe to ${selectedPlan.productName}`
                        : "Select a plan"}
                  </button>
                </>
              ) : (
                <>
                  <p style={{ fontSize: "13px", color: "#888", marginBottom: "16px" }}>
                    One-time purchase. Credits never expire and stack with your subscription.
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "16px" }}>
                    {topups.map((pkg) => (
                      <div
                        key={pkg.id}
                        onClick={() => setSelectedTopupId(pkg.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "14px 16px",
                          borderRadius: "10px",
                          border: selectedTopupId === pkg.id ? "2px solid #000" : "1px solid #eee",
                          cursor: "pointer",
                          transition: "border-color 0.15s",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <div
                            style={{
                              width: "18px",
                              height: "18px",
                              borderRadius: "50%",
                              border: selectedTopupId === pkg.id ? "5px solid #000" : "2px solid #ccc",
                              transition: "border 0.15s",
                            }}
                          />
                          <div style={{ fontSize: "15px", fontWeight: 600 }}>
                            {pkg.amount} Credits
                          </div>
                        </div>
                        <span style={{ fontSize: "18px", fontWeight: 700 }}>
                          {pkg.formattedPrice}
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => handleCheckout(selectedTopupId)}
                    disabled={createCheckoutMutation.isPending || !selectedTopup || packagesLoading}
                    style={{
                      width: "100%",
                      padding: "12px",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#fff",
                      backgroundColor: createCheckoutMutation.isPending || !selectedTopup ? "#ccc" : "#000",
                      border: "none",
                      borderRadius: "10px",
                      cursor: createCheckoutMutation.isPending || !selectedTopup ? "not-allowed" : "pointer",
                      transition: "background-color 0.15s",
                    }}
                  >
                    {createCheckoutMutation.isPending
                      ? "Loading..."
                      : selectedTopup
                        ? `Buy ${selectedTopup.amount} Credits`
                        : "Select a pack"}
                  </button>
                </>
              )}
            </div>

            {/* Footer actions */}
            <div style={{ textAlign: "center" }}>
              <button
                onClick={handleSignOut}
                style={{
                  background: "none",
                  border: "none",
                  color: "#999",
                  fontSize: "13px",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#555")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#999")}
              >
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
